'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';

type HeadlineStyle = 'Professional' | 'Creative' | 'Results-Focused' | 'Keyword-Rich';

interface GeneratedHeadline {
  text: string;
  style: HeadlineStyle;
}

const LINKEDIN_CHAR_LIMIT = 220;

/* ------------------------------------------------------------------ */
/*  Utility helpers                                                    */
/* ------------------------------------------------------------------ */

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/* ------------------------------------------------------------------ */
/*  Word banks used to fill template slots                             */
/* ------------------------------------------------------------------ */

const results = [
  'Growth', 'Revenue', 'Impact', 'Efficiency', 'Innovation',
  'Performance', 'Transformation', 'Results', 'Success', 'Scalability',
];

const verbs = [
  'achieve', 'unlock', 'accelerate', 'maximize', 'streamline',
  'transform', 'scale', 'optimize', 'elevate', 'deliver',
];

const outcomes = [
  'measurable results', 'sustainable growth', 'operational excellence',
  'competitive advantage', 'next-level performance', 'breakthrough outcomes',
  'strategic wins', 'lasting impact', 'higher ROI', 'business success',
];

const problems = [
  'complexity', 'bottlenecks', 'uncertainty', 'chaos', 'inefficiency',
  'stagnation', 'data overload', 'siloed teams', 'missed targets', 'slow processes',
];

const solutions = [
  'clarity', 'scalable systems', 'actionable insights', 'streamlined workflows',
  'high-performing teams', 'strategic direction', 'measurable outcomes',
  'data-driven decisions', 'repeatable processes', 'sustainable growth',
];

const audiences = [
  'teams', 'organizations', 'startups', 'enterprises', 'leaders',
  'businesses', 'companies', 'founders', 'professionals', 'clients',
];

const inputs = [
  'data', 'ideas', 'challenges', 'strategy', 'vision',
  'raw data', 'concepts', 'ambiguity', 'requirements', 'goals',
];

const outputs = [
  'results', 'growth', 'revenue', 'impact', 'actionable strategy',
  'winning products', 'clear roadmaps', 'delighted customers',
  'scalable solutions', 'real-world impact',
];

/* ------------------------------------------------------------------ */
/*  Template-based headline generator                                  */
/* ------------------------------------------------------------------ */

function generateHeadlines(
  role: string,
  industry: string,
  skillsRaw: string,
  style: HeadlineStyle,
): GeneratedHeadline[] {
  const skills = skillsRaw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const s1 = skills[0] || 'Strategy';
  const s2 = skills[1] || 'Leadership';
  const s3 = skills[2] || 'Communication';

  const ind = industry || 'Tech';

  // Build a large pool of templates, then pick 10 unique ones
  const templates: { text: string; style: HeadlineStyle }[] = [
    // --- Professional ---
    {
      text: `${role} | Helping ${ind} teams ${pickRandom(verbs)} ${pickRandom(outcomes)} through ${s1}`,
      style: 'Professional',
    },
    {
      text: `${role} | ${s1} + ${s2} + ${s3} | ${ind} Industry`,
      style: 'Professional',
    },
    {
      text: `${role} | Delivering ${pickRandom(outcomes)} for ${ind} ${pickRandom(audiences)}`,
      style: 'Professional',
    },
    {
      text: `Senior ${role} | ${ind} | ${s1} & ${s2} Specialist`,
      style: 'Professional',
    },

    // --- Creative ---
    {
      text: `Passionate ${role} | Turning ${pickRandom(inputs)} into ${pickRandom(outputs)} for ${ind} ${pickRandom(audiences)}`,
      style: 'Creative',
    },
    {
      text: `From ${pickRandom(problems)} to ${pickRandom(solutions)} | ${role} specializing in ${s1}`,
      style: 'Creative',
    },
    {
      text: `Building ${pickRandom(outcomes)} through ${s1} | ${role} in ${ind}`,
      style: 'Creative',
    },
    {
      text: `${role} who turns ${pickRandom(problems)} into ${pickRandom(solutions)} | ${ind} enthusiast`,
      style: 'Creative',
    },

    // --- Results-Focused ---
    {
      text: `${pickRandom(results)}-Driven ${role} | ${s1} Expert | ${ind}`,
      style: 'Results-Focused',
    },
    {
      text: `${role} | 10+ Years Driving ${pickRandom(results)} in ${ind}`,
      style: 'Results-Focused',
    },
    {
      text: `${role} helping ${ind} ${pickRandom(audiences)} ${pickRandom(verbs)} ${pickRandom(outcomes)} | ${s1} Specialist`,
      style: 'Results-Focused',
    },
    {
      text: `${pickRandom(results)} & ${pickRandom(results)} | ${role} Empowering ${ind} ${pickRandom(audiences)}`,
      style: 'Results-Focused',
    },

    // --- Keyword-Rich ---
    {
      text: `${s1} & ${s2} Expert | ${role} | Open to Opportunities`,
      style: 'Keyword-Rich',
    },
    {
      text: `${ind} ${role} | ${s1}, ${s2}, ${s3} | Let's Connect`,
      style: 'Keyword-Rich',
    },
    {
      text: `${role} | ${s1} | ${s2} | ${s3} | ${ind} Professional`,
      style: 'Keyword-Rich',
    },
    {
      text: `${capitalize(s1)} | ${capitalize(s2)} | ${capitalize(s3)} | ${role} in ${ind}`,
      style: 'Keyword-Rich',
    },
  ];

  // When a specific style is chosen, prioritise that style but still mix in others
  let pool: typeof templates;
  if (style === 'Professional') {
    const primary = templates.filter((t) => t.style === 'Professional');
    const secondary = templates.filter((t) => t.style !== 'Professional');
    pool = [...primary, ...secondary];
  } else if (style === 'Creative') {
    const primary = templates.filter((t) => t.style === 'Creative');
    const secondary = templates.filter((t) => t.style !== 'Creative');
    pool = [...primary, ...secondary];
  } else if (style === 'Results-Focused') {
    const primary = templates.filter((t) => t.style === 'Results-Focused');
    const secondary = templates.filter((t) => t.style !== 'Results-Focused');
    pool = [...primary, ...secondary];
  } else {
    const primary = templates.filter((t) => t.style === 'Keyword-Rich');
    const secondary = templates.filter((t) => t.style !== 'Keyword-Rich');
    pool = [...primary, ...secondary];
  }

  // Deduplicate and take first 10
  const seen = new Set<string>();
  const headlines: GeneratedHeadline[] = [];
  for (const t of pool) {
    if (seen.has(t.text)) continue;
    seen.add(t.text);
    headlines.push({ text: t.text, style: t.style });
    if (headlines.length === 10) break;
  }

  return headlines;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function LinkedInHeadlineContent() {
  const [role, setRole] = useState('');
  const [industry, setIndustry] = useState('');
  const [skills, setSkills] = useState('');
  const [style, setStyle] = useState<HeadlineStyle>('Professional');
  const [headlines, setHeadlines] = useState<GeneratedHeadline[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!role.trim()) return;
    const result = generateHeadlines(role.trim(), industry.trim(), skills, style);
    setHeadlines(result);
    setHasGenerated(true);
  };

  const styleBadgeColor: Record<HeadlineStyle, string> = {
    Professional: 'bg-blue-100 text-blue-700',
    Creative: 'bg-purple-100 text-purple-700',
    'Results-Focused': 'bg-amber-100 text-amber-700',
    'Keyword-Rich': 'bg-teal-100 text-teal-700',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="LinkedIn Headline Generator"
        description="Generate 10 compelling LinkedIn headline options in seconds. Enter your role, industry, and skills to get attention-grabbing headlines optimized for LinkedIn search."
        category="LinkedIn"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <form
          onSubmit={handleGenerate}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            Tell us about yourself
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Role */}
            <div className="md:col-span-2">
              <label
                htmlFor="role"
                className="block text-sm font-semibold text-gray-700 mb-1.5"
              >
                Current or Target Role <span className="text-red-500">*</span>
              </label>
              <input
                id="role"
                type="text"
                required
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g., Product Manager, Data Engineer, UX Designer"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              />
            </div>

            {/* Industry */}
            <div>
              <label
                htmlFor="industry"
                className="block text-sm font-semibold text-gray-700 mb-1.5"
              >
                Industry
              </label>
              <input
                id="industry"
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g., FinTech, Healthcare, SaaS"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              />
            </div>

            {/* Style */}
            <div>
              <label
                htmlFor="style"
                className="block text-sm font-semibold text-gray-700 mb-1.5"
              >
                Headline Style
              </label>
              <select
                id="style"
                value={style}
                onChange={(e) => setStyle(e.target.value as HeadlineStyle)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
              >
                <option value="Professional">Professional</option>
                <option value="Creative">Creative</option>
                <option value="Results-Focused">Results-Focused</option>
                <option value="Keyword-Rich">Keyword-Rich</option>
              </select>
            </div>

            {/* Skills */}
            <div className="md:col-span-2">
              <label
                htmlFor="skills"
                className="block text-sm font-semibold text-gray-700 mb-1.5"
              >
                Key Skills / Specialties
              </label>
              <textarea
                id="skills"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                placeholder="e.g., project management, Python, data analysis — comma-separated"
                rows={3}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
              />
            </div>
          </div>

          {/* Generate Button */}
          <button
            type="submit"
            className="mt-8 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
          >
            Generate Headlines
          </button>
        </form>

        {/* Results */}
        {hasGenerated && headlines.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-6">
              10 LinkedIn Headlines
            </h2>

            <div className="space-y-4">
              {headlines.map((headline, idx) => {
                const charCount = headline.text.length;
                const isOverLimit = charCount > LINKEDIN_CHAR_LIMIT;

                return (
                  <div
                    key={idx}
                    className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-200 hover:shadow-md transition-all"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                      {/* Headline text */}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 leading-relaxed break-words">
                          {headline.text}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {/* Character count badge */}
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              isOverLimit
                                ? 'bg-red-100 text-red-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {charCount} / {LINKEDIN_CHAR_LIMIT} chars
                          </span>

                          {/* Style tag */}
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styleBadgeColor[headline.style]}`}
                          >
                            {headline.style}
                          </span>
                        </div>
                      </div>

                      {/* Copy button */}
                      <div className="flex-shrink-0">
                        <CopyButton text={headline.text} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Educational Section */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Your LinkedIn Headline Matters</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Your LinkedIn headline is the most visible piece of text on your entire profile. It appears every time you show up in search results, send a connection request, comment on a post, or appear in someone&apos;s feed. LinkedIn gives you 220 characters to work with -- and most people waste them by defaulting to their job title alone.
            </p>
            <p>
              A strong headline does three things: it tells people what you do, it communicates the value you deliver, and it includes keywords that recruiters actually search for. According to LinkedIn, profiles with descriptive, keyword-rich headlines appear significantly more often in recruiter searches. This means the difference between &quot;Marketing Manager&quot; and &quot;Marketing Manager | Driving 3x ROI Through Data-Driven Campaigns | B2B SaaS&quot; can be the difference between being found and being invisible.
            </p>
            <p>
              The best headlines use pipe separators (|) to organize distinct value points, include 2-3 industry-specific keywords, and go beyond the job title to communicate a value proposition. Whether you are actively job searching, building your personal brand, or simply wanting to be discovered by the right people, your headline is where it starts.
            </p>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.linkedin.com/help/linkedin/answer/a522735" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">LinkedIn Help: Profile Best Practices</a>
              {' · '}
              <a href="https://blog.linkedin.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">LinkedIn Official Blog</a>
              {' · '}
              <a href="https://www.indeed.com/career-advice/resumes-cover-letters/linkedin-headline-examples" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: LinkedIn Headline Examples</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Your headline gets them to click. Your resume gets you hired."
          description="Create a tailored, ATS-optimized resume in minutes with Superpower Resume."
          buttonText="Build My Resume Free"
        />
      </div>
    </div>
  );
}
